import { Transition } from "lib/headlessui/components/transition/transition";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import * as C from "@sp-crm/core";
import { UserPreferences } from "@sp-crm/core";
import { CalendarDateInput } from "components/ui/calendar-date";
import { Checkbox } from "components/ui/checkbox";
import { defaultLinkStyle } from "components/ui/link";
import { Radio } from "components/ui/radio";
import { TextArea } from "components/ui/textarea";
import * as React from "react";
import { useTenantSettings } from "store/selectors/settings";

export function defaultClientPDFOptions(
    client: C.IClient,
    userPreferences: UserPreferences,
): C.ClientIntakeCustomizationOptions {
    const userSettingsIncludeAdditionalContacts =
        userPreferences.showAdditionalContactsForReferrals;
    const userSettingsIncludeContactPhoneNumbers =
        userPreferences.showContactPhoneNumbersForReferrals;
    const userSettingsIncludeContactEmailAddrs =
        userPreferences.showContactEmailAddrsForReferrals;
    const userSettingsIncludeContactLocations =
        userPreferences.showContactLocationsForReferrals;
    const userSettingsIncludeClientBudget = userPreferences.showClientBudget;
    const userSettingsIncludeClientLocation = userPreferences.showClientLocation;
    const userSettingsIncludeCareProviders =
        userPreferences.showCareProvidersForReferrals;
    const userSettingsIncludeHealthInsurance =
        userPreferences.showHealthInsuranceForReferrals;
    return {
        includeAdditionalContacts: userSettingsIncludeAdditionalContacts,
        includeContactPhoneNumbers: userSettingsIncludeContactPhoneNumbers,
        includeContactEmailAddrs: userSettingsIncludeContactEmailAddrs,
        includeContactLocations: userSettingsIncludeContactLocations,
        includeMyContact: true,
        includeClientBudget: userSettingsIncludeClientBudget,
        includeClientLocation: userSettingsIncludeClientLocation,
        includeCareProviders: userSettingsIncludeCareProviders,
        includeHealthInsurance: userSettingsIncludeHealthInsurance,
        notesOption: "notesField",
        dateStamp: C.CalendarDate.today,
        freeformNotes: "",
        prebuiltNotes: {
            sameForBoth: client.additionalClient
                ? client.additionalClient.otherDetailsAnswersSameAsPrimaryClient
                : false,
            primaryClientNotes: client.otherHealthNotes,
            additionalClientNotes:
                client.additionalClient &&
                client.additionalClient.otherDetailsAnswersSameAsPrimaryClient === false
                    ? client.additionalClient.otherHealthNotes
                    : null,
        },
        filename: "",
    };
}

interface PdfCustomizationProps {
    options: C.ClientIntakeCustomizationOptions;
    callback: (newVal: C.ClientIntakeCustomizationOptions) => void;
}

export const ClientReferralPdfCustomization: React.FunctionComponent<
    PdfCustomizationProps
> = props => {
    const { settings } = useTenantSettings();
    const monthlyBudgetEnabled = settings.showClientMonthlyBudget;
    const [isVisible, setIsVisible] = React.useState(false);
    const toggleVisible = React.useCallback(
        (
            e?: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>,
        ) => {
            if (e && e.preventDefault) {
                e.preventDefault();
            }
            setIsVisible(v => !v);
        },
        [setIsVisible],
    );
    const addedTextChange = (str: string) => {
        const newOptions = { ...props.options, freeformNotes: str };
        props.callback(newOptions);
    };

    const inputControl: () => JSX.Element = () => {
        const freeform = (
            <div>
                <p className="text-sm text-gray-600 mb-2">
                    Type additional details for this PDF. These will not be saved to the
                    client&apos;s profile.
                </p>
                <TextArea
                    rows={4}
                    value={props.options.freeformNotes}
                    onChange={e => addedTextChange(e.target.value)}
                />
            </div>
        );

        const notesField = (
            <div>
                <p className="text-sm text-gray-600 mb-2">
                    Additional details from this client&apos;s profile will be included in
                    the PDF.
                </p>
                <TextArea
                    disabled={true}
                    rows={4}
                    value={props.options.prebuiltNotes.primaryClientNotes}
                />
                {props.options.prebuiltNotes.additionalClientNotes !== null && (
                    <div className="mt-2">
                        <TextArea
                            disabled={true}
                            rows={4}
                            value={props.options.prebuiltNotes.additionalClientNotes}
                        />
                    </div>
                )}
            </div>
        );

        const none = (
            <div>
                <p className="text-sm text-gray-600">
                    No additional details will be included in the PDF.
                </p>
            </div>
        );

        if (props.options.notesOption === "freeform") {
            return freeform;
        } else if (props.options.notesOption === "notesField") {
            return notesField;
        } else {
            return none;
        }
    };

    return (
        <div>
            <div className="mb-2">
                <button
                    onClick={toggleVisible}
                    className="flex items-center space-x-2 cursor-pointer">
                    <h3>Optional Client / Contact Details</h3>
                    <ChevronDownIcon
                        className={`w-6 h-6 transform transition duration-50 ${
                            isVisible ? "rotate-180" : "rotate-0"
                        }`}
                    />
                </button>
                {!isVisible ? (
                    <a
                        href="#"
                        className={`text-sm ${defaultLinkStyle}`}
                        onClick={toggleVisible}>
                        Show advanced options
                    </a>
                ) : null}
            </div>
            <Transition
                as="div"
                enter="transition-all duration-100 ease-in transform"
                enterFrom="opacity-0 -translate-y-16"
                enterTo="opacity-100 translate-y-0"
                leave="transition-all duration-75 ease-in transform"
                leaveTo="opacity-0 -translate-y-8"
                leaveFrom="opacity-100 translate-y-0"
                show={isVisible}>
                <div className="pdf-dialog-panel--section">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Contact Information
                    </h4>
                    <div className="space-y-1">
                        <Checkbox
                            checked={props.options.includeMyContact}
                            onChange={e =>
                                props.callback({
                                    ...props.options,
                                    includeMyContact: e.target.checked,
                                })
                            }
                            label={
                                <>
                                    My details
                                    <span className="text-sm text-gray-500 ml-1">
                                        (name, phone, email)
                                    </span>
                                </>
                            }
                        />
                        <Checkbox
                            checked={props.options.includeAdditionalContacts}
                            onChange={e =>
                                props.callback({
                                    ...props.options,
                                    includeAdditionalContacts: e.target.checked,
                                })
                            }
                            label="Additional contacts' names"
                        />
                        <Checkbox
                            checked={props.options.includeContactEmailAddrs}
                            onChange={e =>
                                props.callback({
                                    ...props.options,
                                    includeContactEmailAddrs: e.target.checked,
                                })
                            }
                            label="Contacts' email addresses"
                        />
                        <Checkbox
                            checked={props.options.includeContactPhoneNumbers}
                            onChange={e =>
                                props.callback({
                                    ...props.options,
                                    includeContactPhoneNumbers: e.target.checked,
                                })
                            }
                            label="Contacts' phone numbers"
                        />
                        <Checkbox
                            checked={props.options.includeContactLocations}
                            onChange={e =>
                                props.callback({
                                    ...props.options,
                                    includeContactLocations: e.target.checked,
                                })
                            }
                            label="Contacts' location details"
                        />
                    </div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 mt-4">
                        Client Information
                    </h4>
                    <div className="space-y-1">
                        {monthlyBudgetEnabled ? (
                            <Checkbox
                                checked={props.options.includeClientBudget}
                                onChange={e =>
                                    props.callback({
                                        ...props.options,
                                        includeClientBudget: e.target.checked,
                                    })
                                }
                                label="Monthly budget"
                            />
                        ) : null}
                        <Checkbox
                            checked={props.options.includeClientLocation}
                            onChange={e =>
                                props.callback({
                                    ...props.options,
                                    includeClientLocation: e.target.checked,
                                })
                            }
                            label="Current location"
                        />
                        {settings.showPhysicianContacts ? (
                            <Checkbox
                                checked={props.options.includeCareProviders}
                                onChange={e =>
                                    props.callback({
                                        ...props.options,
                                        includeCareProviders: e.target.checked,
                                    })
                                }
                                label="Care providers"
                            />
                        ) : null}
                        {settings.showInsuranceInfo ? (
                            <Checkbox
                                checked={props.options.includeHealthInsurance}
                                onChange={e =>
                                    props.callback({
                                        ...props.options,
                                        includeHealthInsurance: e.target.checked,
                                    })
                                }
                                label="Health insurance"
                            />
                        ) : null}
                    </div>
                </div>
                <div className="pdf-dialog-panel--section">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 mt-4">
                        Additional Details
                    </h4>
                    <div className="pdf-dialog-options space-y-1">
                        <Radio
                            onChange={() =>
                                props.callback({
                                    ...props.options,
                                    notesOption: "none",
                                })
                            }
                            checked={props.options.notesOption === "none"}
                            label={`Don't include "Additional Details"`}
                        />
                        <Radio
                            onChange={() =>
                                props.callback({
                                    ...props.options,
                                    notesOption: "notesField",
                                })
                            }
                            checked={props.options.notesOption === "notesField"}
                            label={`Include "Additional Details" from client's profile`}
                        />
                        <Radio
                            onChange={() =>
                                props.callback({
                                    ...props.options,
                                    notesOption: "freeform",
                                })
                            }
                            checked={props.options.notesOption === "freeform"}
                            label={`Type custom "Additional Details" for this PDF`}
                        />
                    </div>
                    <div className="pdf-dialog-text mt-3">{inputControl()}</div>
                </div>
                <div className="pdf-dialog-panel--section">
                    <CalendarDateInput
                        value={props.options.dateStamp}
                        label="PDF Date Stamp"
                        onChange={e => props.callback({ ...props.options, dateStamp: e })}
                    />
                </div>
            </Transition>
        </div>
    );
};
